/**
 * PATH: apps/quote/src/hooks/use-spreadsheet-grid/useSpreadsheetGrid.ts
 * PURPOSE: Orchestrator hook for Excel-like keyboard navigation, selection, clipboard
 */

import { useCallback, useState, useRef } from 'react'
import type { CellCoord, GridSelection, UseSpreadsheetGridOptions, UseSpreadsheetGridReturn } from './types'
import { createKeyHandler } from './createKeyHandler'

export function useSpreadsheetGrid({
  columns, rowCount, onCellChange, onPaste, onFillDown, onCopy, getCellValue,
}: UseSpreadsheetGridOptions): UseSpreadsheetGridReturn {
  const [activeCell, setActiveCell] = useState<CellCoord | null>(null)
  const [selection, setSelection] = useState<GridSelection | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const cellRefs = useRef<Map<string, React.RefObject<HTMLElement>>>(new Map())
  const colCount = columns.length

  const isHiddenCol = useCallback((col: number) => !!columns[col]?.hidden, [columns])
  const findNearestVisibleCol = useCallback((startCol: number, direction: 1 | -1): number => {
    let c = startCol
    while (c >= 0 && c < colCount && isHiddenCol(c)) c += direction
    return Math.max(0, Math.min(c, colCount - 1))
  }, [colCount, isHiddenCol])

  const getCellRef = useCallback((row: number, col: number) => {
    const key = `${row}-${col}`
    if (!cellRefs.current.has(key)) cellRefs.current.set(key, { current: null } as React.RefObject<HTMLElement>)
    return cellRefs.current.get(key) || null
  }, [])

  const isCellSelected = useCallback((row: number, col: number) => {
    if (!selection) return false
    const minRow = Math.min(selection.start.row, selection.end.row), maxRow = Math.max(selection.start.row, selection.end.row)
    const minCol = Math.min(selection.start.col, selection.end.col), maxCol = Math.max(selection.start.col, selection.end.col)
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol
  }, [selection])

  const isCellActive = useCallback((row: number, col: number) => activeCell ? activeCell.row === row && activeCell.col === col : false, [activeCell])

  const navigateTo = useCallback((row: number, col: number) => {
    const clampedRow = Math.max(0, Math.min(row, rowCount - 1))
    let clampedCol = Math.max(0, Math.min(col, colCount - 1))
    if (isHiddenCol(clampedCol)) {
      const forward = findNearestVisibleCol(clampedCol, 1)
      clampedCol = isHiddenCol(forward) ? findNearestVisibleCol(clampedCol, -1) : forward
    }
    setActiveCell({ row: clampedRow, col: clampedCol })
    setSelection({ start: { row: clampedRow, col: clampedCol }, end: { row: clampedRow, col: clampedCol } })
    setIsEditing(false)
  }, [rowCount, colCount, isHiddenCol, findNearestVisibleCol])

  const startEditing = useCallback(() => {
    if (!activeCell) return
    if (!columns[activeCell.col]?.editable) return
    setIsEditing(true)
    setEditValue(getCellValue?.(activeCell.row, activeCell.col) ?? '')
  }, [activeCell, columns, getCellValue])

  const commitEdit = useCallback(() => {
    if (!activeCell || !isEditing) return
    onCellChange?.(activeCell.row, activeCell.col, editValue)
    setIsEditing(false); setEditValue('')
  }, [activeCell, isEditing, editValue, onCellChange])

  const cancelEdit = useCallback(() => { setIsEditing(false); setEditValue('') }, [])

  const handlePaste = useCallback(async () => {
    if (!activeCell) return
    try { const text = await navigator.clipboard.readText(); const rows = text.split('\n').map(r => r.split('\t')); if (rows.length > 0 && rows[0].length > 0) onPaste?.(activeCell.row, activeCell.col, rows) } catch (err) { console.warn('Failed to read clipboard:', err) }
  }, [activeCell, onPaste])

  const handleCopy = useCallback(async () => {
    if (!selection) return
    try {
      const minRow = Math.min(selection.start.row, selection.end.row), maxRow = Math.max(selection.start.row, selection.end.row)
      const minCol = Math.min(selection.start.col, selection.end.col), maxCol = Math.max(selection.start.col, selection.end.col)
      let data: string[][]
      if (onCopy) { data = onCopy(selection) }
      else if (getCellValue) { data = []; for (let r = minRow; r <= maxRow; r++) { const rowData: string[] = []; for (let c = minCol; c <= maxCol; c++) rowData.push(getCellValue(r, c)); data.push(rowData) } }
      else return
      await navigator.clipboard.writeText(data.map(r => r.join('\t')).join('\n'))
    } catch (err) { console.warn('Failed to write to clipboard:', err) }
  }, [selection, onCopy, getCellValue])

  const handleFillDown = useCallback(() => {
    if (!selection || !getCellValue) return
    const minRow = Math.min(selection.start.row, selection.end.row), maxRow = Math.max(selection.start.row, selection.end.row)
    const minCol = Math.min(selection.start.col, selection.end.col), maxCol = Math.max(selection.start.col, selection.end.col)
    for (let c = minCol; c <= maxCol; c++) { const sourceValue = getCellValue(minRow, c); for (let r = minRow + 1; r <= maxRow; r++) onCellChange?.(r, c, sourceValue) }
  }, [selection, getCellValue, onCellChange])

  const handleKeyDown = useCallback(
    createKeyHandler({
      activeCell, selection, isEditing, editValue, columns, rowCount, colCount,
      setSelection, setIsEditing, setEditValue, navigateTo, startEditing, commitEdit, cancelEdit,
      handlePaste, handleCopy, handleFillDown, onCellChange, onFillDown, findNearestVisibleCol,
    }),
    [activeCell, selection, isEditing, editValue, columns, rowCount, colCount, navigateTo, startEditing, commitEdit, cancelEdit, handlePaste, handleCopy, handleFillDown, onCellChange, onFillDown]
  )

  return {
    activeCell, setActiveCell, selection, setSelection, isEditing, setIsEditing,
    editValue, setEditValue, handleKeyDown, isCellSelected, isCellActive,
    getCellRef, navigateTo, startEditing, commitEdit, cancelEdit,
  }
}

export default useSpreadsheetGrid
